import * as React from 'react';
import { BusinessState } from '../business/business.types';
import Avatar from 'material-ui/Avatar';
import { createNoCacheExtraImageUrl } from '../util/image.util';
import { LOGO_HERE } from '../business/business.constants';
import ArrowDropDown from 'material-ui/svg-icons/navigation/arrow-drop-down';
import { AuthState } from '../auth/auth.types';
import { AdminState } from '../admin/admin.types';
import { isImpersonating } from '../admin/Util';
import IconButton from 'material-ui/IconButton';
import NavigationMenu from 'material-ui/svg-icons/navigation/menu';
import './header.css';
import { Manager, Target, Popper, Arrow } from 'react-popper';
import onClickOutside from 'react-onclickoutside';
import { isAuthTokenExpired } from '../commons/AuthTokenValidator';

const ACCOUNT_OPTION_NUMBER = 4;
const MENU_ITEM_HEIGHT = 50;

type Props = {
  readonly businessState: BusinessState
  readonly toMyInfo: () => void
  readonly toBusinessInfo: () => void
  readonly onClickLogout: () => Promise<Response>
  readonly toLogin: () => Promise<{}>
  readonly revertToInitialState: () => void
  readonly revertToInitialStateExceptAuth: () => void
  readonly toAdminDashboard: () => void
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly toggleSidebarHandler: () => void
  readonly toServiceAgreement: () => void
};

type States = {
  readonly businessLogoUrl: string
  readonly isMyInfoHovered: boolean
  readonly isBusinessInfoHovered: boolean
  readonly isSignoutHovered: boolean
  readonly accountMenuOpen: boolean
  readonly isServiceAgreementHovered: boolean
};

class HeaderComponent extends React.Component<Props, States> {
  
  constructor(props: Props) {
    super(props);

    this.state = {
      businessLogoUrl: props.businessState.business && props.businessState.business.picUrl ?
        props.businessState.business.picUrl :
        '',
      isMyInfoHovered: false,
      isBusinessInfoHovered: false,
      isSignoutHovered: false,
      accountMenuOpen: false,
      isServiceAgreementHovered: false
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps) {
      this.setState({
        businessLogoUrl: nextProps.businessState.business && nextProps.businessState.business.picUrl ?
          nextProps.businessState.business.picUrl :
          ''
      });
    }
  }

  componentWillUnmount() {
    this.setState({accountMenuOpen: false});
  }

  handleClickOutside = () => {
    this.setState({accountMenuOpen: false});
  }

  render() {
    const {
      adminState,
      authState,
      toggleSidebarHandler
    } = this.props;

    return(
      <div
        className="row header align-items-center no-gutters"
        onClick={() => this.state.accountMenuOpen ? 
          this.setState({accountMenuOpen: false}) : 
          null
        }
      >
        <div className="col-2">
          <IconButton
            iconStyle={{width: 36, height: 36}}
            onClick={toggleSidebarHandler}
          >
            <NavigationMenu />
          </IconButton>
        </div>
        <div className="col-10">
          <div className="row justify-content-end no-gutters">
            <div className="col-auto">
              <div className="row justify-content-end">
                <div className="col-auto hover-pointer" onClick={this.toggleAccountMenu}>
                  {this.props.businessState.business ? this.props.businessState.business.name : ''}
                </div>
              </div>
              <div className="row justify-content-end">
                <div className="col-auto hover-pointer" onClick={this.toggleAccountMenu}>
                  {
                    adminState.impersonation.account.id !== 0 ?
                      adminState.impersonation.account.firstName + ' ' + adminState.impersonation.account.lastName : 
                      authState.account ?
                        authState.account.firstName + ' ' + authState.account.lastName :
                        ''
                  }
                </div>
              </div>
            </div>
            <div className="col-auto ml2">
              {<Avatar
                size={55}
                src={this.getBusinessLogoUrl(this.state.businessLogoUrl)}
                onClick={this.toggleAccountMenu}
                className="hover-pointer"
              />}
            </div>
            <div className="col-auto mr3">
              <Manager>
                <Target>
                  <IconButton onClick={this.toggleAccountMenu}>
                    <ArrowDropDown />
                  </IconButton>
                </Target>
                <Popper placement="bottom-end" className="popper">
                  {
                    this.state.accountMenuOpen ?
                    <div
                      style={{
                        background: 'white',
                        color: 'black',
                        height: MENU_ITEM_HEIGHT * ACCOUNT_OPTION_NUMBER,
                        width: 230
                      }}
                      className="animated fadeIn"
                    >
                      {this.renderMenuItems()}
                    </div> :
                    null
                  }
                  <Arrow className="popper__arrow"/>
                </Popper>
              </Manager>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderMenuItems = (): JSX.Element => {
    return(
      <div>
        <div
          onClick={() => this.props.toMyInfo()}
          onMouseEnter={() => this.setState({isMyInfoHovered: true})}
          onMouseLeave={() => this.setState({isMyInfoHovered: false})}
          className={this.state.isMyInfoHovered ? 'menuItemHover' : 'menuItem'}
        >
          My Info
        </div>
        <div
          onClick={() => this.props.toBusinessInfo()}
          onMouseEnter={() => this.setState({isBusinessInfoHovered: true})}
          onMouseLeave={() => this.setState({isBusinessInfoHovered: false})}
          className={this.state.isBusinessInfoHovered ? 'menuItemHover' : 'menuItem'}
        >
          Business Info
        </div>
        <div
          onClick={() => this.props.toServiceAgreement()}
          onMouseEnter={() => this.setState({isServiceAgreementHovered: true})}
          onMouseLeave={() => this.setState({isServiceAgreementHovered: false})}
          className={this.state.isServiceAgreementHovered ? 'menuItemHover' : 'menuItem'}
        >
          Service Agreement
        </div>
        <div
          onClick={this.onClickSingout}
          onMouseEnter={() => this.setState({isSignoutHovered: true})}
          onMouseLeave={() => this.setState({isSignoutHovered: false})}
          className={this.state.isSignoutHovered ? 'menuItemHover' : 'menuItem'}
        >
          Sign Out
        </div>
      </div>
    );
  }

  private onClickSingout = () => {
    const {
      revertToInitialState,
      revertToInitialStateExceptAuth,
      toAdminDashboard,
      onClickLogout,
      toLogin,
      adminState
    } = this.props;

    if (isAuthTokenExpired()) {
      revertToInitialState();
      toLogin();
    } else if (isImpersonating(adminState)) {
      revertToInitialStateExceptAuth();
      return toAdminDashboard();
    } else {
      return onClickLogout()
      .then(response => {
        revertToInitialState();
        toLogin();
      });
    }
  }

  private getBusinessLogoUrl = (businessLogoUrl: string): string =>
    businessLogoUrl ? businessLogoUrl + createNoCacheExtraImageUrl(businessLogoUrl) : LOGO_HERE

  private toggleAccountMenu = (): void => {
    this.setState({accountMenuOpen: !this.state.accountMenuOpen});
  }
}

export default onClickOutside(HeaderComponent);



// WEBPACK FOOTER //
// ./src/header/HeaderComponent.tsx